'use client'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { TypeSelectOption } from './types'

const DEBOUNCE_MS = 300
const MIN_SEARCH_LENGTH = 2

/**
 * Owns the visible option list and its debounced text filtering.
 * The timeout lives in a ref so sibling selects never clear each other's pending search.
 */
export const useSelectSearch = ({ options }: { options: TypeSelectOption[] }) => {
  const [currentOptions, setCurrentOptions] = useState(options)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearPending = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = null
  }, [])

  const resetOptions = useCallback(() => {
    clearPending()
    setCurrentOptions(options)
  }, [clearPending, options])

  const filterOptions = useCallback((value: string) => {
    clearPending()
    timeoutRef.current = setTimeout(() => {
      const isSearchable = typeof value === 'string' && value.length >= MIN_SEARCH_LENGTH
      setCurrentOptions(isSearchable ? options.filter(option => option.name.toLowerCase().includes(value.toLowerCase())) : options)
    }, DEBOUNCE_MS)
  }, [clearPending, options])

  useEffect(() => { setCurrentOptions(options) }, [options])
  useEffect(() => clearPending, [clearPending])

  return { currentOptions, filterOptions, resetOptions }
}
